import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { buildPortalTheme } from '@hopdrive/sdk-theme';
import { focusVisibleComponents, type FocusRingPalette } from '@hopdrive/sdk-ui';
import { generateColorPalette } from '@utils/colorUtils';

// The embed renders the shared portal theme, so the packages it now consumes
// (feature-product-shell, feature-order-index, feature-settings-ui) find the slots
// their renderers read — palette.hero, palette.orderStatus, palette.product, the
// body3/body4/PageHeader/SectionHeader typography variants, and the Paper
// custom/customPrint variants the move-details screens use.
//
// The MUI type augmentation for those slots lives in types/portal-theme.d.ts (a
// module augmentation binds to the copy of @mui/material it resolves from, and this
// workspace resolves its own, so the merge is repeated there rather than here).
//
// The embed's two branding knobs (?primaryColor, ?fontFamily) are deep-merged over
// the built portal theme instead of replacing it, so everything the embed does not
// explicitly re-point stays the portal baseline. buildPortalTheme's own `branding`
// option is not used: it gates on a customer's `effective_config.branding.active`
// and derives shades with MUI's augmentColor, whereas the embed's knobs are URL
// params expanded by the app's own generateColorPalette ramp, and it models no font.
const portalBase = buildPortalTheme('light', { app: 'dealer' });

/**
 * The overrides that re-point the theme's font.
 *
 * The portal theme pins its font on individual typography variants (the headings,
 * button) and on the MuiTypography variant styles, so setting `typography.fontFamily`
 * alone would leave those on Inter Tight. The pinned slots are discovered from the
 * built theme rather than restated, so this keeps working if the portal theme adds
 * or drops one.
 */
function fontFamilyOverrides(base: Theme, fontFamily: string): ThemeOptions {
  const typography: Record<string, unknown> = { fontFamily };
  for (const [key, value] of Object.entries(base.typography)) {
    if (value && typeof value === 'object' && 'fontFamily' in value) {
      typography[key] = { fontFamily };
    }
  }

  // MUI's deepmerge replaces arrays wholesale rather than merging them, so the
  // portal's MuiTypography variants are rebuilt from the base list.
  const variants = (base.components?.MuiTypography?.variants ?? []).map(variant =>
    variant.style && typeof variant.style === 'object' && 'fontFamily' in variant.style
      ? { ...variant, style: { ...variant.style, fontFamily } }
      : variant
  );

  return { typography, components: { MuiTypography: { variants } } };
}

/**
 * The keyboard focus ring (#2168, #2176, #2186, #2188), laid over the portal theme.
 *
 * Every component override upstream's hand-written embed theme carried alongside the
 * ring — MuiButton's three heights, MuiChip's `size: 'small'`, MuiIconButton's 4px,
 * MuiMenuItem's spacing, the `contained` + `disableElevation` button defaults — is now
 * `buildPortalTheme`'s, so the shared one wins and only the ring is applied here. The
 * pairing of `disableElevation` with `variant: 'contained'` is exactly what cancels the
 * contained variant's one focus affordance (#2160), so the ring has to be laid over the
 * built theme rather than hoped for.
 *
 * `'light'` rather than a mode variable because the embed has one palette: nothing sets
 * `palette.mode` and `MuiThemeProvider.tsx` hands this theme to `ThemeProvider` with no
 * toggle.
 *
 * The palette is handed in (#2179), and for the embed that is the whole point:
 * `createCustomTheme(primaryColor)` runs a customer's hex through `generateColorPalette`
 * at runtime, so the fill a contained button paints is not a colour in this repository,
 * and black fails every fill under relative luminance 0.10. Deriving the ink from the
 * *constructed* theme is what gives a customer's dark brand colour a white ring instead
 * of none. `apps/embed/src/mui/__tests__/themeFocusVisible.test.tsx` recomputes all of it
 * from the built themes, so a palette edit that breaks it goes red rather than quiet.
 */
function ringPaletteOf(theme: Theme): FocusRingPalette {
  return {
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
    error: theme.palette.error,
    warning: theme.palette.warning,
    info: theme.palette.info,
    success: theme.palette.success,
    background: { paper: theme.palette.background.paper, default: theme.palette.background.default },
  };
}

type RootOverride = Record<string, unknown> | ((props: never) => Record<string, unknown>);

/** The base root override with the ring block spread last, whether the base is an object or a
 *  callback. The portal theme's `MuiButton` root is an `({ ownerState }) =>` callback returning
 *  one of three heights, and MUI's deep merge would replace that callback with the ring object
 *  and drop the heights. Spreading the ring last also puts `&.Mui-focusVisible` after the flat
 *  properties, which a specificity tie needs. */
function ringed(baseRoot: RootOverride | undefined, ring: Record<string, unknown>): RootOverride {
  if (typeof baseRoot === 'function') {
    return (props: never) => ({ ...baseRoot(props), ...ring });
  }
  return { ...(baseRoot ?? {}), ...ring };
}

/** The theme with the ring derived from its own palette spread into every family member's
 *  `styleOverrides.root`. Applied to the baseline theme and to each white-labelled one. */
function withFocusRing(theme: Theme): Theme {
  const focusVisible = focusVisibleComponents('light', ringPaletteOf(theme));
  const merged: Record<string, { styleOverrides?: { root?: RootOverride } & Record<string, unknown> } & Record<string, unknown>> = {
    ...(theme.components as Record<string, never> | undefined),
  };
  for (const [name, override] of Object.entries(focusVisible)) {
    const existing = merged[name] ?? {};
    merged[name] = {
      ...existing,
      styleOverrides: {
        ...existing.styleOverrides,
        root: ringed(existing.styleOverrides?.root, override.styleOverrides.root as Record<string, unknown>),
      },
    };
  }
  return { ...theme, components: merged as Theme['components'] };
}

const portalTheme = withFocusRing(portalBase);

/**
 * Creates the embed theme with optional custom primary color and font family.
 * @param primaryColor - Optional hex color string (e.g., '#ff4830' or 'ff4830')
 * @param fontFamily - Optional font family string (e.g., '"Roboto", sans-serif')
 * @returns A MUI theme object
 */
export function createCustomTheme(primaryColor?: string, fontFamily?: string): Theme {
  if (!primaryColor && !fontFamily) return portalTheme;

  const overrides: ThemeOptions = {};
  if (primaryColor) overrides.palette = { primary: generateColorPalette(primaryColor) };
  if (fontFamily) Object.assign(overrides, fontFamilyOverrides(portalBase, fontFamily));

  // The ring is derived after the merge, off the branded palette, not before it.
  return withFocusRing(createTheme(portalBase, overrides));
}

// Create the default theme
const theme = createCustomTheme();

export default theme;
